import type {ReactElement, SyntheticEvent} from 'react'
import {cloneElement, isValidElement, useCallback, useEffect, useId, useRef} from 'react'

import styles from './SidebarTooltip.module.css'

export interface SidebarTooltipProps {
  /** Tooltip text shown when the sidebar is collapsed. */
  text: string
  /** Disable the tooltip without remounting its trigger. */
  disabled?: boolean
  /**
   * A single React element that acts as the tooltip trigger. Event handlers
   * and `aria-describedby` are cloned onto it so no wrapper element is
   * injected into the DOM — the child remains the direct list child for
   * Primer ActionList/NavList compatibility.
   */
  children: ReactElement
}

/**
 * Lightweight tooltip for collapsible sidebar items. Replaces Primer's
 * `<Tooltip>` to avoid the flash-on-hover bug caused by Primer's popover
 * `::after` bridge pseudo-element intercepting pointer events.
 *
 * Uses `popover="auto"` so the tooltip renders in the top layer
 * (escaping `overflow: clip` on the sidebar) with native Escape
 * dismissal (WCAG 1.4.13) and `pointer-events: none` so it never
 * steals mouse events from trigger elements.
 *
 * Use `disabled` while expanded to keep the trigger mounted across state changes.
 */
export function SidebarTooltip({text, children, disabled = false}: SidebarTooltipProps) {
  const tooltipRef = useRef<HTMLSpanElement>(null)
  const openTimeoutRef = useRef<ReturnType<typeof setTimeout>>(undefined)
  const tooltipId = useId()

  const showTooltip = useCallback((e: SyntheticEvent) => {
    const trigger = e.currentTarget as HTMLElement
    openTimeoutRef.current = setTimeout(() => {
      const tooltip = tooltipRef.current
      if (!tooltip) return
      const rect = trigger.getBoundingClientRect()
      tooltip.style.top = `${rect.top + rect.height / 2}px`
      tooltip.style.left = `${rect.right + 8}px`
      tooltip.style.transform = 'translateY(-50%)'
      try {
        tooltip.showPopover()
      } catch {
        /* already open or not connected */
      }
    }, 200)
  }, [])

  const hideTooltip = useCallback(() => {
    clearTimeout(openTimeoutRef.current)
    try {
      tooltipRef.current?.hidePopover()
    } catch {
      /* already hidden or not connected */
    }
  }, [])

  useEffect(() => {
    return () => clearTimeout(openTimeoutRef.current)
  }, [disabled])

  if (!isValidElement(children)) return children

  const childProps = children.props as Record<string, unknown>

  const trigger = disabled
    ? children
    : // eslint-disable-next-line @eslint-react/no-clone-element, react-hooks/refs
      cloneElement(children, {
        onMouseEnter: showTooltip,
        onMouseLeave: hideTooltip,
        onFocus: showTooltip,
        onBlur: hideTooltip,
        'aria-describedby': [childProps['aria-describedby'], tooltipId].filter(Boolean).join(' '),
      } as Record<string, unknown>)

  return (
    <>
      {trigger}
      {!disabled && (
        <span ref={tooltipRef} id={tooltipId} role="tooltip" popover="auto" className={styles.sidebarTooltip}>
          {text}
        </span>
      )}
    </>
  )
}
